// Deterministic uniform spatial hash for neighbor queries over moving bodies (the enemy swarm).
// Lets us replace the O(n^2) all-pairs loops that forced the 320-enemy cap with O(n·k) neighbor
// scans, so the screen can actually fill like Megabonk. Pure + deterministic: integer cell keys,
// candidate indices appended in ascending (insertion) order, so iteration order is stable
// run-to-run — no Math.random, no floating Map-order dependence in callers.

export interface SpatialGrid {
  cell: number
  buckets: Map<number, number[]>
}

const OFFSET = 1 << 15 // shift cell coords non-negative for a collision-free integer key
const STRIDE = 1 << 16

function cellKey(cx: number, cz: number): number {
  return (cx + OFFSET) * STRIDE + (cz + OFFSET)
}

/** Build a uniform grid of point indices; indices land in their bucket in ascending order. */
export function buildSpatialGrid(pts: ArrayLike<{ x: number; z: number }>, cell: number): SpatialGrid {
  const buckets = new Map<number, number[]>()
  const inv = 1 / cell
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]
    const k = cellKey(Math.floor(p.x * inv), Math.floor(p.z * inv))
    let b = buckets.get(k)
    if (!b) {
      b = []
      buckets.set(k, b)
    }
    b.push(i)
  }
  return { cell, buckets }
}

/**
 * Append the point indices in the 3×3 cell neighborhood of (x,z) to `out` (caller clears it).
 * With cell ≥ the largest pair interaction distance, this is a superset of all points that can
 * interact with one at (x,z), so neighbor scans match an all-pairs scan over interacting pairs.
 */
export function gridNeighbors(grid: SpatialGrid, x: number, z: number, out: number[]): void {
  const inv = 1 / grid.cell
  const cx = Math.floor(x * inv)
  const cz = Math.floor(z * inv)
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      const b = grid.buckets.get(cellKey(cx + dx, cz + dz))
      if (b) for (let i = 0; i < b.length; i++) out.push(b[i])
    }
  }
}
